// @ts-nocheck
// Estudo 2.0 — Threads de discussão (parentCommentId real, citar-fala embutida no texto, reply encadeado).
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { IcoQuote, IcoClose, IcoSend, IcoReply, IcoEdit, IcoTrash, IcoTouch, IcoFlame, IcoLock } from './icons2';
import { Avatar, inlineRich } from './ui2';
import { useStudy } from './cards2';
import { MentionInput } from '../../mention-input';

export function buildTree(comments: any[]) {
  const byParent: any = {};
  comments.forEach((c) => { (byParent[c.parentCommentId || 'root'] ||= []).push(c); });
  return byParent;
}

const truncate = (s: string, n: number) => s.length > n ? s.slice(0, n).trim() + '…' : s;

/* Renderiza o corpo do comentário: se começa com "> trecho", mostra como citação. */
function CommentBody({ text }: any) {
  const lines = (text || '').split('\n');
  if (lines[0]?.startsWith('> ')) {
    const quote: string[] = [];
    let i = 0;
    while (i < lines.length && lines[i].startsWith('> ')) { quote.push(lines[i].slice(2)); i++; }
    const rest = lines.slice(i).join('\n').replace(/^\n+/, '');
    return (
      <div>
        <div style={{ margin: '6px 0', padding: '5px 10px', background: 'var(--paper)', borderRadius: 9, borderLeft: '2px solid var(--plum)' }}>
          <div style={{ fontSize: 12.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>“{quote.join(' ')}”</div>
        </div>
        {rest && <div style={{ fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink-2)', marginTop: 3 }}>{inlineRich(rest, 'cr')}</div>}
      </div>
    );
  }
  return <div style={{ fontSize: 14.5, lineHeight: 1.5, color: 'var(--ink-2)', marginTop: 3 }}>{inlineRich(text, 'ct')}</div>;
}

/* composer de comentário/resposta — multilinha que cresce */
export function CommentComposer({ onSubmit, autoFocus, placeholder, quoting, onCancelQuote }: any) {
  const [val, setVal] = useState('');
  const ref = useRef<any>(null);
  const grow = useCallback(() => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 160) + 'px';
  }, []);
  useEffect(() => { if (autoFocus && ref.current) ref.current.focus(); }, [autoFocus]);
  useEffect(grow, [val, grow]);
  const send = () => {
    const t = val.trim(); if (!t) return;
    // se citando, embute o trecho como "> ..." no início (persiste no texto, sem campo novo)
    const finalText = quoting ? `> ${truncate(quoting.text, 140)}\n\n${t}` : t;
    onSubmit(finalText); setVal(''); if (ref.current) ref.current.style.height = 'auto';
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {quoting && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', background: 'var(--paper)', borderRadius: 10, borderLeft: '2px solid var(--ember)' }}>
          <IcoQuote size={13} style={{ color: 'var(--ember)', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--ember-2)', marginBottom: 1 }}>citando {quoting.author}</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{quoting.text}</div>
          </div>
          <button onClick={onCancelQuote} style={{ color: 'var(--ink-4)', padding: 2 }}><IcoClose size={14} /></button>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, background: 'var(--paper)', border: '1px solid var(--line-strong)', borderRadius: 14, padding: '6px 6px 6px 12px' }}>
        <MentionInput multiline inputRef={ref} value={val} onChange={setVal} rows={1} onSubmit={send} dropdownPosition="above"
          placeholder={placeholder || 'Escreva uma resposta…'} wrapStyle={{ flex: 1, minWidth: 0 }} inputStyle={{
            width: '100%', background: 'none', border: 'none', outline: 'none', resize: 'none',
            color: 'var(--ink)', fontSize: 14.5, lineHeight: 1.45, padding: '6px 0', maxHeight: 160,
          }} />
        <button onClick={send} disabled={!val.trim()} style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0, display: 'grid', placeItems: 'center',
          background: val.trim() ? 'var(--ember)' : 'var(--paper-3)', color: val.trim() ? '#1a1208' : 'var(--ink-4)',
          transition: 'background 0.2s', alignSelf: 'flex-end',
        }}><IcoSend size={16} /></button>
      </div>
    </div>
  );
}

const threadActionStyle = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--ink-3)', padding: '4px 8px', borderRadius: 8, fontWeight: 500 };
const miniReact = (active: boolean, color: string) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 500, color: active ? color : 'var(--ink-3)', padding: '4px 8px', borderRadius: 8, background: active ? 'color-mix(in oklab, ' + color + ' 14%, transparent)' : 'transparent' });

function CommentNode({ c, byParent, depth, viewerId, replyOpen, setReplyOpen, onSubmitReply, onEdit, onDelete, onReactComment }: any) {
  const { canPost, onOpenProfile } = useStudy() || {};
  const goAuthor = (e: any) => { e?.stopPropagation?.(); if (onOpenProfile && c.author?.id) onOpenProfile(c.author.id); };
  const kids = byParent[c.id] || [];
  const mine = c.author?.id === viewerId;
  const indent = depth > 0;
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(c.text || '');
  return (
    <div style={{ position: 'relative', paddingLeft: indent ? 14 : 0, marginTop: depth === 0 ? 0 : 12 }}>
      {indent && <div style={{ position: 'absolute', left: 0, top: 4, bottom: 8, width: 2, borderRadius: 2, background: 'var(--line-strong)' }} />}
      <div style={{ display: 'flex', gap: 10 }}>
        <span onClick={goAuthor} style={{ cursor: onOpenProfile ? 'pointer' : 'default', lineHeight: 0, flexShrink: 0 }}><Avatar user={c.author} size={depth === 0 ? 32 : 28} /></span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
            <span onClick={goAuthor} style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--ink)', cursor: onOpenProfile ? 'pointer' : 'default' }}>{c.author?.name}</span>
            <span className="mono" style={{ fontSize: 11, color: 'var(--ink-4)' }}>· {c.timeAgo}</span>
          </div>
          {editing ? (
            <div style={{ marginTop: 6 }}>
              <MentionInput multiline value={editVal} onChange={setEditVal} rows={3} dropdownPosition="below" inputStyle={{ width: '100%', background: 'var(--paper)', border: '1px solid var(--line-strong)', borderRadius: 10, padding: '8px 10px', color: 'var(--ink)', fontSize: 14, lineHeight: 1.5, resize: 'none', outline: 'none' }} />
              <div style={{ display: 'flex', gap: 8, marginTop: 6, justifyContent: 'flex-end' }}>
                <button onClick={() => { setEditing(false); setEditVal(c.text || ''); }} style={{ fontSize: 13, color: 'var(--ink-3)', padding: '6px 10px' }}>Cancelar</button>
                <button onClick={() => { if (editVal.trim()) { onEdit(c.id, editVal.trim()); setEditing(false); } }} style={{ fontSize: 13, fontWeight: 600, color: 'var(--ember)', padding: '6px 10px' }}>Salvar</button>
              </div>
            </div>
          ) : <CommentBody text={c.text} />}
          {!editing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 7, flexWrap: 'wrap' }}>
              {canPost && <>
              <button onClick={() => onReactComment && onReactComment(c.id, 'tocou')} style={miniReact((c.yourReactions || []).includes('tocou'), 'var(--ember)')}><IcoTouch size={13} fill={(c.yourReactions || []).includes('tocou') ? 'var(--ember)' : 'none'} />{(c.reactions?.tocou || 0) > 0 && <span>{c.reactions.tocou}</span>}</button>
              <button onClick={() => onReactComment && onReactComment(c.id, 'fogo')} style={miniReact((c.yourReactions || []).includes('fogo'), 'var(--ember-2)')}><IcoFlame size={13} fill={(c.yourReactions || []).includes('fogo') ? 'var(--ember-2)' : 'none'} />{(c.reactions?.fogo || 0) > 0 && <span>{c.reactions.fogo}</span>}</button>
              <button onClick={() => setReplyOpen(replyOpen === c.id + ':quote' ? null : c.id + ':quote')} style={threadActionStyle}><IcoQuote size={13} />Citar</button>
              <button onClick={() => setReplyOpen(replyOpen === c.id ? null : c.id)} style={threadActionStyle}><IcoReply size={13} />Responder</button>
              </>}
              {mine && <button onClick={() => setEditing(true)} style={threadActionStyle}><IcoEdit size={13} />Editar</button>}
              {mine && <button onClick={() => onDelete(c.id)} style={{ ...threadActionStyle, color: '#E0796B' }}><IcoTrash size={13} />Excluir</button>}
            </div>
          )}
          {(replyOpen === c.id || replyOpen === c.id + ':quote') && (
            <div style={{ marginTop: 10 }}>
              <CommentComposer autoFocus placeholder={`Responder a ${(c.author?.name || '').split(' ')[0]}…`}
                quoting={replyOpen === c.id + ':quote' ? { author: c.author?.name, text: truncate(c.text.replace(/^>.*\n+/, ''), 90) } : null}
                onCancelQuote={() => setReplyOpen(c.id)}
                onSubmit={(t: string) => { onSubmitReply(t, c.id); setReplyOpen(null); }} />
            </div>
          )}
        </div>
      </div>
      {kids.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {kids.map((k: any) => (
            <CommentNode key={k.id} c={k} byParent={byParent} depth={Math.min(depth + 1, 4)}
              viewerId={viewerId} replyOpen={replyOpen} setReplyOpen={setReplyOpen}
              onSubmitReply={onSubmitReply} onEdit={onEdit} onDelete={onDelete} onReactComment={onReactComment} />
          ))}
        </div>
      )}
    </div>
  );
}

export function ThreadView({ comments, onAddComment, onEditComment, onDeleteComment, onReactComment, viewerId, title }: any) {
  const { canPost } = useStudy() || {};
  const [replyOpen, setReplyOpen] = useState<any>(null);
  const byParent = useMemo(() => buildTree(comments), [comments]);
  const roots = byParent.root || [];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {title || 'Discussão'} · {comments.length}
        </span>
        <div style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {roots.length === 0 && (
          <div style={{ textAlign: 'center', padding: '10px 0 18px', color: 'var(--ink-4)', fontSize: 13.5 }}>
            Ninguém forjou aqui ainda. Abra a discussão.
          </div>
        )}
        {roots.map((c: any) => (
          <CommentNode key={c.id} c={c} byParent={byParent} depth={0} viewerId={viewerId}
            replyOpen={replyOpen} setReplyOpen={setReplyOpen}
            onSubmitReply={(t: string, pid: string) => onAddComment(t, pid)}
            onEdit={onEditComment} onDelete={onDeleteComment} onReactComment={onReactComment} />
        ))}
      </div>
      <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
        {canPost
          ? <CommentComposer placeholder="Adicione sua leitura…" onSubmit={(t: string) => onAddComment(t, null)} />
          : <div style={{ textAlign: 'center', color: 'var(--ink-4)', fontSize: 13.5, padding: '4px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}><IcoLock size={14} />Entre no grupo pra comentar.</div>}
      </div>
    </div>
  );
}
